import { SecretTypeEnum } from '@/types/secret';

export const SECRETS_TAB = 'secrets';

export type SettingsTab = 'apiKeys' | 'secrets' | 'settings';

export const SETTINGS_TAB_VALUES: readonly SettingsTab[] = [ 'apiKeys', 'secrets', 'settings' ] as const;

export function isSettingsTab( value: string | null | undefined ): value is SettingsTab {
  return value === 'apiKeys' || value === 'secrets' || value === 'settings';
}

// URL-friendly aliases for SecretTypeEnum. The enum values stay SHOUTY since
// they're part of the aidp-core API contract; only the URL representation is
// kebab-cased so links stay readable.
const TYPE_TO_PARAM: Record<SecretTypeEnum, string> = {
  [ SecretTypeEnum.AGENT ]: 'agent',
  [ SecretTypeEnum.EVALS ]: 'evals',
  [ SecretTypeEnum.DATA_PLATFORM ]: 'data-platform'
};

const PARAM_TO_TYPE: Record<string, SecretTypeEnum> = Object.fromEntries(
  Object.entries( TYPE_TO_PARAM ).map( ( [ enumValue, paramValue ] ) => [ paramValue, enumValue as SecretTypeEnum ] )
);

export function secretTypeToParam( type: SecretTypeEnum ): string {
  return TYPE_TO_PARAM[type];
}

export function paramToSecretType( value: string | null | undefined ): SecretTypeEnum | null {
  if ( !value ) return null;
  return PARAM_TO_TYPE[value] ?? null;
}

/**
 * /settings deep-link to a specific tab. Used by the sidebar and any other
 * caller that wants to send the user straight to a Settings tab without
 * concatenating the query string by hand.
 */
export function settingsTabPath( tab: SettingsTab ): string {
  const params = new URLSearchParams();
  params.set( 'tab', tab );
  return `/settings?${ params.toString() }`;
}

/**
 * Build the search-param shape used to deep-link into the /settings Secrets
 * tab pre-filtered to a single Data Platform secret. Centralises the keys so
 * every caller (CredentialsSection, future ones) stays in lockstep with the
 * settings page's URL contract.
 */
export function dataPlatformSecretLinkParams( secretName: string ): URLSearchParams {
  const params = new URLSearchParams();
  params.set( 'tab', SECRETS_TAB );
  params.set( 'type', secretTypeToParam( SecretTypeEnum.DATA_PLATFORM ) );
  if ( secretName ) {
    params.set( 'name', secretName );
  }
  return params;
}

export function dataPlatformSecretLinkPath( secretName: string ): string {
  return `/settings?${ dataPlatformSecretLinkParams( secretName ).toString() }`;
}

/**
 * /settings deep-link to the Secrets tab pre-filtered by type=Data Platform,
 * with no specific secret. Use when sending the user to "go pick or create a
 * Data Platform secret" rather than a specific row.
 */
export function dataPlatformSecretsTabPath(): string {
  return dataPlatformSecretLinkPath( '' );
}
